import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import parse from "html-react-parser";
import { useT } from "@transifex/react";
import { Modal } from "storybook/components/modal";
import type { ExitModalConfig } from "../../context/questionnaire/types";
import {
  StartOverModalActions,
  StartOverModalButtonPrimary,
  StartOverModalButtonSecondary,
} from "../../layouts/questionnaire-layout/styles";
import styled from "styled-components";

/** Bottom slack when deciding the sentinel has entered the scrollport (subpixel / padding). */
const SENTINEL_ROOT_MARGIN = "0px 0px 8px 0px";

const Sentinel = styled.div`
  height: 1px;
  width: 100%;
  flex-shrink: 0;
  pointer-events: none;
`;

/** div: Modal.Head / MUI DialogTitle already wraps heading markup — avoid nested h2. */
const ExitModalTitle = styled.div`
  font-family: var(--font-family-default);
  font-size: 1.5rem;
  font-weight: 600;
  color: #1a202c;
  margin: 0;
  line-height: 1.4;
  text-align: left;
`;

const ScrollBody = styled.div`
  max-height: min(50vh, 320px);
  overflow-y: auto;
  margin-top: 8px;
  padding-right: 4px;
  word-break: break-word;
`;

const PlainBody = styled.div`
  white-space: pre-wrap;
  font-size: 15px;
  line-height: 1.5;
`;

const ErrorText = styled.p`
  color: #b91c1c;
  font-size: 14px;
  margin: 0;
`;

function effectiveContentType(
  config: ExitModalConfig
): "text/plain" | "text/html" {
  if (config.content_type) return config.content_type;
  return config.content_url ? "text/plain" : "text/html";
}

/** Delphi JSON may stringify booleans; only explicit false opts out of scroll-to-agree. */
function isReadAllEnforced(config: ExitModalConfig): boolean {
  const v = config.read_all as unknown;
  if (v === false || v === "false" || v === 0 || v === "0") return false;
  return true;
}

export interface ExitConsentModalProps {
  open: boolean;
  config: ExitModalConfig | null;
  defaultAgreeLabel: string;
  defaultDeclineLabel: string;
  onAgree: () => void;
  onDecline: () => void;
}

const ExitConsentModal: React.FC<ExitConsentModalProps> = ({
  open,
  config,
  defaultAgreeLabel,
  defaultDeclineLabel,
  onAgree,
  onDecline,
}) => {
  const t = useT();
  const scrollRef = useRef<HTMLDivElement>(null);
  const sentinelRef = useRef<HTMLDivElement>(null);
  const [fetchedBody, setFetchedBody] = useState<string | null>(null);
  const [fetchState, setFetchState] = useState<
    "idle" | "loading" | "ok" | "error"
  >("idle");
  const [scrollComplete, setScrollComplete] = useState(false);

  const readAllEnforced = config ? isReadAllEnforced(config) : false;

  useEffect(() => {
    if (!open || !config) return;

    setFetchedBody(null);

    if (!config.content_url) {
      setFetchState("ok");
      return;
    }

    let cancelled = false;
    setFetchState("loading");

    fetch(config.content_url, { credentials: "omit" })
      .then((res) => {
        if (!res.ok) throw new Error(String(res.status));
        return res.text();
      })
      .then((text) => {
        if (!cancelled) {
          setFetchedBody(text);
          setFetchState("ok");
        }
      })
      .catch(() => {
        if (!cancelled) setFetchState("error");
      });

    return () => {
      cancelled = true;
    };
  }, [open, config]);

  const effectiveBody =
    config && config.content_url ? fetchedBody ?? "" : config?.body ?? "";

  const contentReady =
    !!config &&
    (!config.content_url || fetchState === "ok" || fetchState === "error");

  const loadFailed = !!config?.content_url && fetchState === "error";
  const loadPending =
    !!config?.content_url && fetchState !== "ok" && fetchState !== "error";

  /**
   * Scroll-to-agree: use a bottom sentinel + IntersectionObserver(root: scroll body).
   * Fixes short content (no inner scroll) and cases where the dialog scrolls instead of
   * firing onScroll on this div — scrollHeight heuristics + onScroll alone could leave Agree stuck.
   */
  useLayoutEffect(() => {
    if (!open || !contentReady) return;
    if (!readAllEnforced) {
      setScrollComplete(true);
      return;
    }

    let io: IntersectionObserver | null = null;
    let cancelled = false;

    const setup = (): boolean => {
      const root = scrollRef.current;
      const target = sentinelRef.current;
      if (!root || !target) return false;
      root.scrollTop = 0;
      setScrollComplete(false);
      io = new IntersectionObserver(
        (entries) => {
          if (entries.some((e) => e.isIntersecting)) setScrollComplete(true);
        },
        { root, rootMargin: SENTINEL_ROOT_MARGIN, threshold: 1 }
      );
      io.observe(target);
      return true;
    };

    if (!setup()) {
      const id = window.requestAnimationFrame(() => {
        if (!cancelled) setup();
      });
      return () => {
        cancelled = true;
        window.cancelAnimationFrame(id);
        io?.disconnect();
      };
    }

    return () => {
      cancelled = true;
      io?.disconnect();
    };
  }, [open, contentReady, effectiveBody, readAllEnforced]);

  const agreeLabel = config?.agree_label || defaultAgreeLabel;
  const declineLabel = config?.decline_label || defaultDeclineLabel;

  const agreeEnabled =
    !!config && !loadPending && !loadFailed && scrollComplete;

  const handleAgreeClick = useCallback(() => {
    if (!agreeEnabled) return;
    onAgree();
  }, [agreeEnabled, onAgree]);

  const bodyType = config ? effectiveContentType(config) : "text/plain";

  return (
    <Modal
      theme="default"
      open={open && !!config}
      onClose={() => undefined}
      padding="24px"
      disableBackdropClick
      hideCloseButton
      width="560px"
    >
      <Modal.Head alignTitle="left">
        <ExitModalTitle>{config?.title || t("Please review")}</ExitModalTitle>
      </Modal.Head>
      <Modal.Body>
        {loadPending && <p>{t("Loading...")}</p>}
        {loadFailed && (
          <ErrorText>
            {t(
              "Unable to load this content. You can go back or exit the survey."
            )}
          </ErrorText>
        )}
        {!loadPending && !loadFailed && (
          <ScrollBody ref={scrollRef} data-testid="exit-modal-scroll">
            {bodyType === "text/plain" ? (
              <PlainBody>{effectiveBody}</PlainBody>
            ) : (
              <div>{parse(effectiveBody || "")}</div>
            )}
            <Sentinel
              ref={sentinelRef}
              aria-hidden
              data-testid="exit-modal-sentinel"
            />
          </ScrollBody>
        )}
      </Modal.Body>
      <Modal.Actions>
        <StartOverModalActions>
          <StartOverModalButtonSecondary type="button" onClick={onDecline}>
            {declineLabel}
          </StartOverModalButtonSecondary>
          <StartOverModalButtonPrimary
            type="button"
            onClick={handleAgreeClick}
            disabled={!agreeEnabled}
            data-testid="exit-modal-agree"
          >
            {agreeLabel}
          </StartOverModalButtonPrimary>
        </StartOverModalActions>
      </Modal.Actions>
    </Modal>
  );
};

export default ExitConsentModal;
